import * as THREE from "three";
import { Engine } from "../../game/engine";
import { AudioBed } from "../../game/audio";

/**
 * Ascent — vertical platform-chain, redesigned for 3D depth + engagement.
 * Game 10 of 12. (v2 — feel-pass sweep.)
 *
 * Scoring contract preserved: launches[] + landings[] + seed-driven
 * platforms replay unchanged. Visual layer rebuilt: 3D box platforms,
 * sphere avatar with trail, trajectory preview, biome cycling per altitude.
 */

export const G = 18;
export const POWER_MIN = 0.2;
export const POWER_MAX = 1.5;
export const VX_PER_POWER = 5.0;
export const VY_PER_POWER = 12.0;
export const PLATFORM_GAP = 3.0;
export const MAX_PLATFORMS = 60;
export const CAMERA_SPEED = 6;

// v5: zone metadata mirrored from scoring-ascent.ts. Zones drive the
// per-zone palette shift + zone-entry banner; the server is authoritative
// for the ZONE_BONUS award.
const ZONE_THRESHOLDS: readonly number[] = [0, 12, 24, 36, 48];
const ZONE_NAMES: readonly string[] = ["Foothills", "Forest", "Cliffs", "Spires", "Heavens"];
const ZONE_BONUS = 600;
function zoneForPlatformIdx(idx: number): number {
  let z = 0;
  for (let i = ZONE_THRESHOLDS.length - 1; i >= 0; i--) {
    if (idx >= ZONE_THRESHOLDS[i]) { z = i; break; }
  }
  return z;
}

export interface AscentLaunch { t: number; charge_ms: number; aim_x: number; platform_idx_from: number }
export interface AscentLanding { t: number; platform_idx: number; x_at_landing: number }

export interface AscentCallbacks {
  onHud: (state: {
    height: number;
    scoreEstimate: number;
    falling: boolean;
    glyphsCollected: number;
    bonusPadLandings: number;
    timeLeftMs: number | null; // null = endless mode
  }) => void;
  onGameOver: (result: {
    launches: AscentLaunch[];
    landings: AscentLanding[];
    scoreEstimate: number;
    height: number;
    glyphsCollected: number;
    /** v5: count of glyph-bearing platforms the avatar reached during the
     *  run (idx ≤ height). Used for the "K/N glyphs" recap. */
    glyphsInReach: number;
    bonusPadLandings: number;
    mode: "endless" | "time";
  }) => void;
  /** v5: fired once when the avatar first reaches a new zone
   *  (Foothills/Forest/Cliffs/Spires/Heavens). Bootstrap renders a
   *  banner — matches the milestone-banner pattern from Spire/Drift. */
  onZoneEnter?: (info: { zone: number; name: string; bonus: number }) => void;
}

export type AscentMode = "endless" | "time";
export const TIME_TRIAL_MS = 60_000;

interface BonusPad { x_center: number; width: number; mesh: THREE.Mesh; mat: THREE.MeshStandardMaterial; }
interface Platform {
  idx: number;
  y: number;
  x_center: number;
  width: number;
  mesh: THREE.Mesh;
  mat: THREE.MeshStandardMaterial;
  bonus_pad?: BonusPad;
  has_glyph: boolean;
  glyph?: THREE.Mesh;
  glyphMat?: THREE.MeshStandardMaterial;
  /** Has the glyph been collected this run? (cosmetic — server is authoritative) */
  glyph_collected?: boolean;
}

const BONUS_PAD_EVERY = 5;
const GLYPH_EVERY = 8;

interface ZonePalette { platform: number; emissive: number; sky: number; fog: number; accent: number; }
// v5: one palette per zone, aligned 1:1 with ZONE_THRESHOLDS. The progression
// — warm dawn → forest → cold cliffs → violet spires → gold heavens — gives
// each zone a distinct atmospheric identity. Was previously a 4-palette
// cycle on every 12 platforms, which read as "biome change" but had no
// scoring meaning.
const ZONE_PALETTES: ZonePalette[] = [
  { platform: 0xff9c5b, emissive: 0xcc5530, sky: 0x180a05, fog: 0x24120a, accent: 0xffd54a }, // Foothills — warm
  { platform: 0x5bff7a, emissive: 0x1faa3a, sky: 0x041208, fog: 0x0a1a10, accent: 0xa8ffc0 }, // Forest — green
  { platform: 0x9ab0c8, emissive: 0x40556a, sky: 0x06080c, fog: 0x0a0e18, accent: 0xc8dcf0 }, // Cliffs — grey
  { platform: 0x9a72ff, emissive: 0x4030aa, sky: 0x0a0518, fog: 0x150a28, accent: 0xc94bff }, // Spires — violet
  { platform: 0xffd54a, emissive: 0xcc9020, sky: 0x18120a, fog: 0x241a05, accent: 0xfff0a0 }, // Heavens — gold
];

function makeRng(seed: number): () => number {
  let s = seed | 0;
  return () => {
    s = (s + 0x6d2b79f5) | 0;
    let t = s;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

export class Ascent {
  private engine: Engine;
  private audio = new AudioBed();
  private platforms: Platform[] = [];
  private avatar: THREE.Mesh;
  private avatarHalo: THREE.Mesh;
  private trail: THREE.Mesh[] = [];
  private state: "ready" | "charging" | "flying" | "over" = "ready";
  private curIdx = 0;
  private avatarPos = new THREE.Vector2();
  private chargeStart = 0;
  private flightStart = 0;
  private flightLaunchPos = new THREE.Vector2();
  private flightVel = new THREE.Vector2();
  private pointerX = 0;
  private launches: AscentLaunch[] = [];
  private landings: AscentLanding[] = [];
  private startedAt = 0;
  private mode: AscentMode = "endless";
  private glyphsCollected = 0;
  private bonusPadLandings = 0;
  private scoreEstimate = 0;
  private cb: AscentCallbacks;
  private canvas: HTMLCanvasElement;
  private seed: number;
  private trajectoryLine: THREE.Line;
  private trajectoryMat: THREE.LineBasicMaterial;
  // v5: zone-driven palette blend. currentZone is what the scene reads
  // as; targetZone is what zoneForPlatformIdx says it should be; the
  // blend eases from current → target over ~2.5s when they differ.
  private currentZone = 0;
  private targetZone = 0;
  private zoneBlend = 0;
  private zonesEntered = new Set<number>([0]);

  // v6 — three vertical-parallax starfield layers in world space. Camera
  // climbs y as the avatar ascends, so fixed-y stars scroll downward
  // through the frame; near layer (z=-55) scrolls fastest, far (z=-200)
  // is the slow stellar back-curtain. Y distribution biased upward
  // (mostly above start) so the player ascends INTO the field. Per
  // memory feedback-sibling-games-shallow §"real 3D scene with depth
  // cues". Mirrors Phalanx 2bc3ebc.
  private starLayers: THREE.Points[] = [];

  private onPointerDown = () => this.beginCharge();
  private onPointerUp = () => this.releaseLaunch();
  private onPointerMove = (e: PointerEvent) => this.updateAim(e);
  private onKeyDown = (e: KeyboardEvent) => {
    if (e.code === "Space") {
      e.preventDefault();
      if (this.state === "ready") this.beginCharge();
    } else if (e.code === "KeyA") this.pointerX = -2;
    else if (e.code === "KeyD") this.pointerX = 2;
  };
  private onKeyUp = (e: KeyboardEvent) => {
    if (e.code === "Space") this.releaseLaunch();
  };

  constructor(canvas: HTMLCanvasElement, cb: AscentCallbacks, seed = Math.floor(Math.random() * 0x7fffffff)) {
    this.canvas = canvas;
    this.cb = cb;
    this.seed = seed;

    this.engine = new Engine(canvas, { onFrame: (dt, t) => this.frame(dt, t) });
    this.engine.camera.position.set(3, 6, 14);
    this.engine.camera.lookAt(0, 6, 0);
    this.engine.camera.fov = 58;
    this.engine.camera.updateProjectionMatrix();
    this.engine.scene.fog = new THREE.Fog(ZONE_PALETTES[0].fog, 18, 50);
    this.engine.scene.background = new THREE.Color(ZONE_PALETTES[0].sky);

    const ambient = new THREE.AmbientLight(0x6080a0, 0.45);
    this.engine.scene.add(ambient);
    const key = new THREE.DirectionalLight(0xfff0d0, 1.0);
    key.position.set(6, 10, 8);
    this.engine.scene.add(key);
    const rim = new THREE.DirectionalLight(0x4060ff, 0.5);
    rim.position.set(-6, 4, 4);
    this.engine.scene.add(rim);

    // Avatar — emissive sphere with halo ring.
    const aGeo = new THREE.SphereGeometry(0.35, 20, 14);
    const aMat = new THREE.MeshStandardMaterial({ color: 0xffffff, emissive: 0x66ddff, emissiveIntensity: 1.5, roughness: 0.25, metalness: 0.6 });
    this.avatar = new THREE.Mesh(aGeo, aMat);
    this.engine.scene.add(this.avatar);

    const haloGeo = new THREE.RingGeometry(0.5, 0.85, 24);
    const haloMat = new THREE.MeshBasicMaterial({ color: 0x66ddff, transparent: true, opacity: 0.55, blending: THREE.AdditiveBlending, side: THREE.DoubleSide });
    this.avatarHalo = new THREE.Mesh(haloGeo, haloMat);
    this.engine.scene.add(this.avatarHalo);

    // Trajectory preview line (visible during charge).
    const trajGeo = new THREE.BufferGeometry();
    trajGeo.setAttribute("position", new THREE.Float32BufferAttribute(new Array(60).fill(0), 3));
    this.trajectoryMat = new THREE.LineBasicMaterial({ color: 0x46ffe0, transparent: true, opacity: 0, blending: THREE.AdditiveBlending });
    this.trajectoryLine = new THREE.Line(trajGeo, this.trajectoryMat);
    this.engine.scene.add(this.trajectoryLine);

    this.buildPlatforms();

    // v6 — parallax starfield. Three THREE.Points layers at varying Z
    // depths fill the void behind the platform chain. The vertical bias
    // ((r() - 0.1) * spreadY) places most stars above the avatar's start
    // so the climb reads as ascending INTO the field. Stars are static
    // in world space; the camera follow-y produces the parallax. fog:false
    // so stars remain visible past the linear-fog far plane (50u).
    // Seeded off the run seed for deterministic placement.
    const makeStars = (count: number, z: number, spreadX: number, spreadY: number, opts: { size: number; color: number }) => {
      const positions = new Float32Array(count * 3);
      let s = (seed ^ Math.imul(z | 0, 0x9e3779b1)) | 0;
      const r = () => {
        s = (s + 0x6d2b79f5) | 0;
        let t = s;
        t = Math.imul(t ^ (t >>> 15), t | 1);
        t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
        return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
      };
      for (let i = 0; i < count; i++) {
        positions[i * 3] = (r() - 0.5) * spreadX * 2;
        positions[i * 3 + 1] = (r() - 0.1) * spreadY;
        positions[i * 3 + 2] = z + (r() - 0.5) * 8;
      }
      const geo = new THREE.BufferGeometry();
      geo.setAttribute("position", new THREE.BufferAttribute(positions, 3));
      const mat = new THREE.PointsMaterial({
        color: opts.color, size: opts.size, sizeAttenuation: true,
        transparent: true, opacity: 0.85, depthWrite: false, fog: false,
      });
      const pts = new THREE.Points(geo, mat);
      this.engine.scene.add(pts);
      this.starLayers.push(pts);
    };
    makeStars(500, -200, 200, 300, { size: 0.6, color: 0x7a86a8 });  // far  — cool blue-gray
    makeStars(250, -110, 130, 280, { size: 0.5, color: 0x9aa2c4 });  // mid
    makeStars(100,  -55,  70, 260, { size: 0.4, color: 0xffe0c0 });  // near — warm tint, sparse

    canvas.addEventListener("pointerdown", this.onPointerDown);
    canvas.addEventListener("pointermove", this.onPointerMove);
    window.addEventListener("pointerup", this.onPointerUp);
    window.addEventListener("keydown", this.onKeyDown);
    window.addEventListener("keyup", this.onKeyUp);
  }

  private buildPlatforms() {
    const rng = makeRng(this.seed);
    this.platforms.push(this.makePlatform(0, 0, 0, 5, undefined, false));
    for (let k = 1; k < MAX_PLATFORMS; k++) {
      const y = k * PLATFORM_GAP;
      const x = (rng() - 0.5) * 8;
      const w = Math.max(2.0, 4.5 - k * 0.05);
      // Bonus-pad placement mirrors scoring-ascent.ts generatePlatforms.
      let padSpec: { x_center: number; width: number } | undefined;
      if (k >= BONUS_PAD_EVERY && k % BONUS_PAD_EVERY === 0) {
        const sideRng = rng();
        const sideSign = sideRng < 0.5 ? -1 : 1;
        const offset = 4 + (sideRng * 2 - 1) * 0.5;
        let padX = x + sideSign * offset;
        padX = Math.max(-7, Math.min(7, padX));
        padSpec = { x_center: padX, width: 1.2 };
      } else {
        rng();
      }
      const has_glyph = k >= GLYPH_EVERY && k % GLYPH_EVERY === 0;
      this.platforms.push(this.makePlatform(k, y, x, w, padSpec, has_glyph));
    }
  }

  private makePlatform(
    idx: number,
    y: number,
    x_center: number,
    width: number,
    padSpec: { x_center: number; width: number } | undefined,
    has_glyph: boolean,
  ): Platform {
    const palette = ZONE_PALETTES[zoneForPlatformIdx(idx)];
    const geo = new THREE.BoxGeometry(width, 0.4, 2.4);
    const mat = new THREE.MeshStandardMaterial({
      color: idx === 0 ? 0xff5b3a : palette.platform,
      emissive: idx === 0 ? 0xaa2010 : palette.emissive,
      emissiveIntensity: 0.7,
      roughness: 0.4,
      metalness: 0.5,
    });
    const m = new THREE.Mesh(geo, mat);
    m.position.set(x_center, y, 0);
    this.engine.scene.add(m);

    let bonus_pad: BonusPad | undefined;
    if (padSpec) {
      const padGeo = new THREE.BoxGeometry(padSpec.width, 0.4, 2.0);
      const padMat = new THREE.MeshStandardMaterial({
        color: 0xffd54a,
        emissive: 0xff8800,
        emissiveIntensity: 1.4,
        roughness: 0.3,
        metalness: 0.6,
      });
      const padMesh = new THREE.Mesh(padGeo, padMat);
      padMesh.position.set(padSpec.x_center, y, 0);
      this.engine.scene.add(padMesh);
      bonus_pad = { x_center: padSpec.x_center, width: padSpec.width, mesh: padMesh, mat: padMat };
    }

    let glyph: THREE.Mesh | undefined;
    let glyphMat: THREE.MeshStandardMaterial | undefined;
    if (has_glyph) {
      const glyphGeo = new THREE.OctahedronGeometry(0.45, 0);
      glyphMat = new THREE.MeshStandardMaterial({
        color: 0x46ffe0,
        emissive: 0x00aa90,
        emissiveIntensity: 1.8,
        roughness: 0.25,
        metalness: 0.7,
      });
      glyph = new THREE.Mesh(glyphGeo, glyphMat);
      // Place above main platform unless there's a bonus pad — then place above that.
      const hostX = bonus_pad ? bonus_pad.x_center : x_center;
      glyph.position.set(hostX, y + 1.4, 0);
      this.engine.scene.add(glyph);
    }

    return { idx, y, x_center, width, mesh: m, mat, bonus_pad, has_glyph, glyph, glyphMat };
  }

  private updateAim(e: PointerEvent) {
    const rect = this.canvas.getBoundingClientRect();
    const nx = ((e.clientX - rect.left) / rect.width) * 2 - 1;
    this.pointerX = nx * 8;
  }

  private beginCharge() {
    if (this.state !== "ready") return;
    this.state = "charging";
    this.chargeStart = performance.now();
  }

  private releaseLaunch() {
    if (this.state !== "charging") return;
    const now = performance.now();
    const chargeMs = now - this.chargeStart;
    const t = Math.round(now - this.startedAt);
    const aim_x = this.pointerX - this.avatarPos.x;
    this.launches.push({ t, charge_ms: Math.round(chargeMs), aim_x: Math.round(aim_x * 100) / 100, platform_idx_from: this.curIdx });
    const power = Math.max(POWER_MIN, Math.min(POWER_MAX, chargeMs / 1000));
    const sign = aim_x > 0 ? 1 : aim_x < 0 ? -1 : 0;
    this.flightVel.set(power * VX_PER_POWER * sign, power * VY_PER_POWER);
    this.flightLaunchPos.copy(this.avatarPos);
    this.flightStart = now;
    this.state = "flying";
    this.audio.chime(7, 0);
    this.trajectoryMat.opacity = 0;
  }

  private updateTrajectoryPreview() {
    // Predict the arc the avatar will take given the current charge + aim.
    const chargeMs = performance.now() - this.chargeStart;
    const power = Math.max(POWER_MIN, Math.min(POWER_MAX, chargeMs / 1000));
    const aim_x = this.pointerX - this.avatarPos.x;
    const sign = aim_x > 0 ? 1 : aim_x < 0 ? -1 : 0;
    const vx = power * VX_PER_POWER * sign;
    const vy = power * VY_PER_POWER;
    const pos = this.trajectoryLine.geometry.attributes.position;
    const arr = pos.array as Float32Array;
    const N = 20;
    for (let i = 0; i < N; i++) {
      const tau = (i / (N - 1)) * 1.2;
      arr[i * 3 + 0] = this.avatarPos.x + vx * tau;
      arr[i * 3 + 1] = this.avatarPos.y + vy * tau - 0.5 * G * tau * tau;
      arr[i * 3 + 2] = 0;
    }
    pos.needsUpdate = true;
    this.trajectoryLine.geometry.setDrawRange(0, N);
    this.trajectoryMat.opacity = Math.min(0.7, 0.3 + power / POWER_MAX * 0.5);
    this.trajectoryMat.color.setHex(power >= POWER_MAX * 0.95 ? 0xff5b3a : 0x46ffe0);
  }

  private frame(dt: number, _t: number) {
    if (this.state === "over") return;
    const now = performance.now();
    const runT = now - this.startedAt;
    const cameraFloor = -2 + CAMERA_SPEED * (runT / 1000);

    // v6 — altitude-driven escalation. signal = current platform index /
    // MAX_PLATFORMS (clamped). Feeds (a) engine.setBoost so FOV pulse +
    // afterimage + chromatic ramp with altitude, and (b) audio drone bed
    // intensity (0.15 baseline → 0.7 at ceiling). Per memory
    // feedback-sibling-games-shallow §"escalation hook" + §"post-processing".
    const altSignal = Math.min(1, this.curIdx / MAX_PLATFORMS);
    this.engine.setBoost(altSignal, dt);
    this.audio.setDroneIntensity(0.15 + altSignal * 0.55);

    // v5: zone-aligned palette shift. Fires onZoneEnter once per new zone
    // and eases the sky+fog between palettes over ~2.5s.
    const targetZone = zoneForPlatformIdx(this.curIdx);
    if (targetZone !== this.targetZone) {
      this.currentZone = this.targetZone;
      this.targetZone = targetZone;
      this.zoneBlend = 0;
      if (!this.zonesEntered.has(targetZone)) {
        this.zonesEntered.add(targetZone);
        this.cb.onZoneEnter?.({ zone: targetZone, name: ZONE_NAMES[targetZone], bonus: ZONE_BONUS });
        this.audio.chime(19, -0.3);
        this.audio.chime(24, 0);
        this.audio.chime(31, 0.3);
      }
    }
    if (this.currentZone !== this.targetZone) {
      this.zoneBlend = Math.min(1, this.zoneBlend + dt * 0.4);
      const a = ZONE_PALETTES[this.currentZone];
      const b = ZONE_PALETTES[this.targetZone];
      const t = this.zoneBlend;
      const sky = new THREE.Color(a.sky).lerp(new THREE.Color(b.sky), t);
      const fog = new THREE.Color(a.fog).lerp(new THREE.Color(b.fog), t);
      (this.engine.scene.background as THREE.Color).copy(sky);
      if (this.engine.scene.fog instanceof THREE.Fog) this.engine.scene.fog.color.copy(fog);
      if (this.zoneBlend >= 1) this.currentZone = this.targetZone;
    }

    if (this.state === "flying") {
      const tau = (now - this.flightStart) / 1000;
      this.avatarPos.x = this.flightLaunchPos.x + this.flightVel.x * tau;
      this.avatarPos.y = this.flightLaunchPos.y + this.flightVel.y * tau - 0.5 * G * tau * tau;
      this.avatar.position.set(this.avatarPos.x, this.avatarPos.y, 0);
      this.avatarHalo.position.set(this.avatarPos.x, this.avatarPos.y, -0.2);
      // Tilt avatar with horizontal velocity.
      this.avatar.rotation.z = -Math.atan2(this.flightVel.x, 8) * 0.5;

      // Trail: spawn dim sphere every other frame.
      if (this.trail.length < 30 && Math.random() < 0.6) {
        const tg = new THREE.SphereGeometry(0.16, 8, 6);
        const tm = new THREE.MeshBasicMaterial({ color: 0x66ddff, transparent: true, opacity: 0.55, blending: THREE.AdditiveBlending });
        const tmesh = new THREE.Mesh(tg, tm);
        tmesh.position.copy(this.avatar.position);
        this.engine.scene.add(tmesh);
        this.trail.push(tmesh);
      }
      // Decay older trail particles.
      for (let i = this.trail.length - 1; i >= 0; i--) {
        const t = this.trail[i];
        const mat = t.material as THREE.MeshBasicMaterial;
        mat.opacity -= dt * 1.8;
        t.scale.multiplyScalar(1 - dt * 0.4);
        if (mat.opacity <= 0.01) {
          this.engine.scene.remove(t);
          t.geometry.dispose();
          mat.dispose();
          this.trail.splice(i, 1);
        }
      }

      // Landing check (unchanged math).
      const next = this.platforms[this.curIdx + 1];
      if (next && this.avatarPos.y <= next.y && this.flightVel.y - G * tau < 0) {
        const a = -0.5 * G;
        const b = this.flightVel.y;
        const c = this.flightLaunchPos.y - next.y;
        const disc = b * b - 4 * a * c;
        if (disc >= 0) {
          const tauLand = (-b - Math.sqrt(disc)) / (2 * a);
          const xLand = this.flightLaunchPos.x + this.flightVel.x * tauLand;
          // Check bonus pad first — narrower target, higher reward.
          let landed = false;
          let landedOnPad = false;
          if (next.bonus_pad && Math.abs(xLand - next.bonus_pad.x_center) <= next.bonus_pad.width / 2) {
            landed = true;
            landedOnPad = true;
          } else if (Math.abs(xLand - next.x_center) <= next.width / 2) {
            landed = true;
          }
          if (landed) {
            this.curIdx += 1;
            this.avatarPos.set(xLand, next.y);
            this.flightVel.set(0, 0);
            this.state = "ready";
            const tLand = Math.round(now - this.startedAt);
            this.landings.push({ t: tLand, platform_idx: this.curIdx, x_at_landing: Math.round(xLand * 100) / 100 });
            if (landedOnPad && next.bonus_pad) {
              this.scoreEstimate += 300;
              this.bonusPadLandings += 1;
              next.bonus_pad.mat.emissiveIntensity = 3.0;
              this.audio.chime(19, 0);
            } else {
              const dist = Math.abs(xLand - next.x_center);
              const weight = dist <= 0.3 ? 200 : dist <= 0.8 ? 120 : 60;
              this.scoreEstimate += weight;
              next.mat.emissiveIntensity = 2.0;
              this.audio.chime(dist <= 0.3 ? 12 : 7, 0);
            }
            // Glyph collection: any successful landing (main OR pad) on a
            // platform with a glyph counts. Server is authoritative.
            if (next.has_glyph && !next.glyph_collected) {
              next.glyph_collected = true;
              this.glyphsCollected += 1;
              this.scoreEstimate += 400;
              if (next.glyph && next.glyphMat) {
                next.glyph.scale.setScalar(0);
              }
              this.audio.chime(24, 0);
              this.audio.chime(31, 0.3);
            }
          }
        }
      }
    } else if (this.state === "ready") {
      const p = this.platforms[this.curIdx];
      this.avatarPos.set(p.x_center, p.y + 0.4);
      this.avatar.position.set(this.avatarPos.x, this.avatarPos.y, 0);
      this.avatarHalo.position.set(this.avatarPos.x, this.avatarPos.y, -0.2);
      this.avatar.rotation.z = Math.sin(now * 0.003) * 0.06;
    } else if (this.state === "charging") {
      this.updateTrajectoryPreview();
      // Pulse the avatar to telegraph charge.
      const chargeMs = now - this.chargeStart;
      const power = Math.max(POWER_MIN, Math.min(POWER_MAX, chargeMs / 1000));
      const s = 1 + (power / POWER_MAX) * 0.5 * Math.sin(now * 0.018);
      this.avatar.scale.setScalar(s);
      (this.avatar.material as THREE.MeshStandardMaterial).emissiveIntensity = 1.5 + (power / POWER_MAX) * 1.5;
    }

    // Decay platform emissive pulse back to baseline; spin + bob glyphs.
    for (const p of this.platforms) {
      if (p.mat.emissiveIntensity > 0.7) {
        p.mat.emissiveIntensity = Math.max(0.7, p.mat.emissiveIntensity - dt * 2.5);
      }
      if (p.bonus_pad && p.bonus_pad.mat.emissiveIntensity > 1.4) {
        p.bonus_pad.mat.emissiveIntensity = Math.max(1.4, p.bonus_pad.mat.emissiveIntensity - dt * 2.5);
      }
      if (p.glyph && !p.glyph_collected) {
        p.glyph.rotation.y = now * 0.003;
        p.glyph.rotation.z = now * 0.0015;
        p.glyph.position.y = p.y + 1.4 + Math.sin(now * 0.004 + p.idx) * 0.18;
      }
    }

    // Camera follows the player vertically with a slight horizontal angle for depth.
    this.engine.camera.position.y = Math.max(6, this.avatarPos.y);
    this.engine.camera.position.x = 3 + Math.sin(now * 0.0006) * 0.5;
    this.engine.camera.lookAt(0, this.engine.camera.position.y, 0);

    if (this.avatarPos.y < cameraFloor) this.finish();
    // Time-trial: cap the run at TIME_TRIAL_MS regardless of fall state.
    if (this.mode === "time" && runT >= TIME_TRIAL_MS) {
      this.finish();
    }
    const falling = this.state === "flying" && this.avatarPos.y < this.flightLaunchPos.y - 0.5;
    const timeLeftMs = this.mode === "time" ? Math.max(0, TIME_TRIAL_MS - runT) : null;
    this.cb.onHud({
      height: this.curIdx,
      scoreEstimate: this.scoreEstimate,
      falling,
      glyphsCollected: this.glyphsCollected,
      bonusPadLandings: this.bonusPadLandings,
      timeLeftMs,
    });
  }

  setMode(mode: AscentMode) { this.mode = mode; }

  start() {
    this.audio.arm();
    // v6 — seed at baseline; frame() ramps with altSignal each tick.
    this.audio.setDroneIntensity(0.15);
    this.curIdx = 0;
    this.state = "ready";
    this.avatarPos.set(0, 0.4);
    this.launches = [];
    this.landings = [];
    this.scoreEstimate = 0;
    this.startedAt = performance.now();
    this.currentZone = 0;
    this.targetZone = 0;
    this.zoneBlend = 0;
    this.zonesEntered = new Set<number>([0]);
    // Snap scene back to Foothills palette in case a previous run left it elsewhere.
    (this.engine.scene.background as THREE.Color).setHex(ZONE_PALETTES[0].sky);
    if (this.engine.scene.fog instanceof THREE.Fog) this.engine.scene.fog.color.setHex(ZONE_PALETTES[0].fog);
    this.glyphsCollected = 0;
    this.bonusPadLandings = 0;
    // Reset per-platform collected flags + glyph mesh scales.
    for (const p of this.platforms) {
      p.glyph_collected = false;
      if (p.glyph) p.glyph.scale.setScalar(1);
    }
    for (const t of this.trail) {
      this.engine.scene.remove(t);
      t.geometry.dispose();
      (t.material as THREE.Material).dispose();
    }
    this.trail = [];
    this.engine.start();
  }

  private finish() {
    if (this.state === "over") return;
    this.state = "over";
    this.audio.setDroneIntensity(0);
    this.audio.thud();
    let qSum = 0;
    let qCount = 0;
    for (let i = 0; i < this.landings.length; i++) {
      const ld = this.landings[i];
      const p = this.platforms[ld.platform_idx];
      if (p) {
        const dist = Math.abs(ld.x_at_landing - p.x_center);
        qSum += Math.max(0, 1 - dist / (p.width / 2));
        qCount += 1;
      }
    }
    const avgQ = qCount > 0 ? qSum / qCount : 0;
    const finalScore = Math.round(this.scoreEstimate * (1 + 0.3 * avgQ));
    // v5: count glyph-bearing platforms within reach (idx ≤ height) so the
    // recap can show "K / N glyphs collected" rather than just a raw count.
    let glyphsInReach = 0;
    for (let i = 0; i <= this.curIdx && i < this.platforms.length; i++) {
      if (this.platforms[i].has_glyph) glyphsInReach += 1;
    }
    this.cb.onGameOver({
      launches: this.launches,
      landings: this.landings,
      scoreEstimate: finalScore,
      height: this.curIdx,
      glyphsCollected: this.glyphsCollected,
      glyphsInReach,
      bonusPadLandings: this.bonusPadLandings,
      mode: this.mode,
    });
  }

  setMuted(b: boolean) { this.audio.setMuted(b); }
  setVolume(v: number) { this.audio.setVolume(v); }
  armAudio() { this.audio.arm(); }

  dispose() {
    // v6 — release adaptive drone + tear down parallax-starfield layers
    // so navigate-away → re-mount doesn't accumulate THREE.Points or
    // leave a stuck drone bed.
    this.audio.setDroneIntensity(0);
    for (const layer of this.starLayers) {
      this.engine.scene.remove(layer);
      layer.geometry.dispose();
      (layer.material as THREE.Material).dispose();
    }
    this.starLayers = [];
    this.canvas.removeEventListener("pointerdown", this.onPointerDown);
    this.canvas.removeEventListener("pointermove", this.onPointerMove);
    window.removeEventListener("pointerup", this.onPointerUp);
    window.removeEventListener("keydown", this.onKeyDown);
    window.removeEventListener("keyup", this.onKeyUp);
    this.engine.dispose();
  }
}
